import {TFunction} from 'i18next';
import {Page} from '../../common/constant/page';
import {
  getAdBanner,
  getBadge,
  getContentProps,
  getImageUrl,
  getTextWithEllipsis,
  getTorosInfo,
  I_AdBanner,
  I_Badge,
  I_ContentMeta,
  I_TorosLog,
} from '../../common/state/apiGet';
import {ITicketType} from '../../common/type';
import {I_ApiMeta} from '../../common/util/api';
import {
  DEFAULT_SHORT_DATETIME_FORMAT,
  formatFromServerDate,
  getNowFromServer,
  getRemainText,
  getTimeStamp,
  SHORT_DATE_FORMAT,
} from '../../common/util/date';
import {toCamelCase} from '../../common/util/string';
import {
  getLocalRecentItemByContentIdV4,
  getWaitfreePeriodData,
} from '../../commonSub/function';
import {getWaitfree, I_Waitfree} from '../../commonSub/state/apiGet';
import {
  getEpisodeItem,
  getOriginalProduct,
  getVoucher,
  I_EpisodeItem,
  I_OriginalProduct,
  I_Voucher,
} from '../../commonSub/state/apiGet';
import {I_SearchResult} from '../../search/state/apiGet';
import {getViewerResources, I_ViewerImage} from '../../viewer/state/apiGet';
import {
  IContent,
  IContentProfile,
  IRecommendItem,
  IEpisode,
  IUnivCollection,
  IUnivItemType,
  IPurchase,
  ILastRead,
  IKeywordContent,
  IContentTicket,
  IContentTicketInfo,
  IContentAddedTicketItem,
  IContentUsedTicketItem,
  IContentTicketDetail,
  IViewData,
  ISpecialContent,
  IFreeTicketInfo,
} from '../type';

function getUnivCollections(
  id: number,
  data: I_UnivCollection[] = []
): IUnivCollection[] {
  return data.map(item => {
    const titles = [item.title1, item.title2].map(title =>
      title
        ? getTextWithEllipsis(
            title,
            item.contentTitle
              ? {
                  [CONTENT_TITLE_TOKEN]: item.contentTitle,
                }
              : {}
          )
        : []
    );

    const indexedContents =
      item.contents?.map((content, index) => ({
        ...content,
        index,
      })) || [];
    return {
      collectionId: item.id,
      label: item.label,
      labelBg: item.labelBackgroundColor,
      matchingType: toCamelCase(
        item.matchingType === 'DAILY' ? 'AI' : item.matchingType
      ) as IUnivItemType,
      matchingRule: item.matchingRule,
      title1: titles[0],
      title2: titles[1],
      totalContentCount: item.totalContentCount,
      updated: item.updated,
      contents: indexedContents
        .filter(item => Number(item.id) !== id)
        .map(item => {
          return {
            ...getContentProps(item, [
              'id',
              'sid',
              'title',
              'bgColor',
              'bgColorRgb',
            ]),
            thumbImg: getImageUrl(item.thumbnailImage),
            ordnum: item.index + 1,
          };
        }),
      torosInfo: item.torosLog ? getTorosInfo(item.torosLog) : undefined,
    };
  });
}
const CONTENT_TITLE_TOKEN = 'CONTENT_TITLE';
interface I_UnivCollection {
  id: string;
  label: string;
  labelBackgroundColor: string;
  matchingType: string;
  matchingRule: string;
  order: number;
  title1: string;
  title2?: string;
  contentTitle?: string;
  totalContentCount: number;
  updated: boolean;
  contents: I_UnivContent[];
  torosLog?: I_TorosLog;
}

interface I_UnivContent extends I_ContentMeta {
  thumbnailImage: string;
}

export function getContent({
  id,
  data,
}: {
  id: number;
  data: I_Content;
}): IContent | undefined {
  const contentProps =
    data &&
    getContentProps(
      data,
      [
        'id',
        'sid',
        'title',
        'mainImg',
        'bgImg',
        'genre',
        'synopsis',
        'authors',
        'authorDetails',
        'promotionVideo',
        'viewCount',
        'likeCount',
        'bgColor',
        'bgColorRgb',
        'motionVideo',
        'motionStartImg',
        'isStopContent',
        'shareImg',
        'isAdult',
      ],
      {isBigBg: true}
    );
  return (
    data && {
      ...contentProps,
      univCollections: getUnivCollections(id, data.collections),
      thumbImg: getImageUrl(data.thumbnailImage),
    }
  );
}
interface I_Content extends I_ContentMeta {
  collections: I_UnivCollection[];
  thumbnailImage: string;
}

export function getContentProfile(data: I_ContentProfile): IContentProfile {
  const contentProps =
    data &&
    getContentProps(
      data,
      ['title', 'synopsis', 'authorDetails', 'videoClips', 'badges'],
      {
        badgesFilter: badge =>
          badge.title !== 'WAIT_FOR_FREE_PLUS' &&
          badge.title !== 'WAIT_FOR_FREE' &&
          badge.title !== 'FREE_PUBLISHING',
      }
    );
  return (
    data && {
      ...contentProps,
      keywords: data.seoKeywords || [],
      authorAnotherCollection:
        data.authorAnotherCollection?.map(getRecommendContents) || [],
      recommendationContents:
        data.recommendationContents &&
        getRecommendContents(data.recommendationContents),
      originalProduct:
        data.originalProduct && getOriginalProduct(data.originalProduct),
      specialContent:
        data.specialContent?.bridgePageUrl &&
        (data.specialContent?.characterImage ||
          data.specialContent?.backgroundImage)
          ? getSpecialContent(data.specialContent)
          : undefined,
    }
  );
}

function getSpecialContent(data: I_SpecialContent): ISpecialContent {
  const {bridgePageUrl, titleImage, characterImage, backgroundImage} = data;
  return {
    bridgeUrl: bridgePageUrl,
    titleImg: getImageUrl(titleImage, {hasAlpha: true}),
    thumbImg: getImageUrl(characterImage, {hasAlpha: true}),
    bgImg: getImageUrl(backgroundImage),
  };
}

function getRecommendContents({
  contents,
  title,
  authorName,
  torosLog,
  authorType,
}: I_RecommendItem): IRecommendItem {
  return {
    title: getTextWithEllipsis(
      title,
      authorName
        ? {
            [AUTHOR_NAME_TOKEN]: authorName,
          }
        : {}
    ),
    contents:
      contents?.map(item => {
        return {
          ...getContentProps(
            item,
            [
              'id',
              'sid',
              'thumbImg',
              'bgImg',
              'title',
              'titleImg',
              'badges',
              'bgColorRgb',
            ],
            {
              badgesFilter: badge =>
                (badge.title === '19' && badge.type === 'AGE_LIMIT') ||
                badge.type === 'NEW' ||
                badge.title === 'WAIT_FOR_FREE_PLUS' ||
                badge.title === 'WAIT_FOR_FREE' ||
                badge.title === 'FREE_PUBLISHING',
            }
          ),
        };
      }) || [],
    torosInfo: torosLog ? getTorosInfo(torosLog) : undefined,
    biAuthorType:
      authorType === 'AUTHOR'
        ? 'author'
        : authorType === 'ILLUSTRATOR'
          ? 'illust'
          : undefined,
  };
}

const AUTHOR_NAME_TOKEN = 'AUTHOR';

export function getContentTicket(
  data?: I_ContentTicket
): IContentTicket | undefined {
  const waitfree = data?.waitForFree
    ? getWaitfree(data.waitForFree)
    : undefined;
  return data
    ? {
        ticketCount: data.ticketCount,
        waitfree,
        welcomeTicketCount: data.welcomeGiftTicketCount || 0,
        isFreePublishing: !!data.freePublishing,
        freeEpisodeCount: data.freeEpisodeCount || 0,
        allFree: data.allFree,
        isWaitfreePlus: !!data.waitForFreePlus,
        // 배지는 반드시 1개 또는 0개가 내려온다.
        badge: data?.badges?.map(badge =>
          getBadge({
            ...badge,
            // 충전중일때 badge.title을 별도로 내려주지 않아 Timer를 직접 붙여준다.
            title: waitfree?.isCharging ? `${badge.title}Timer` : badge.title,
          })
        )[0],
      }
    : undefined;
}

interface I_ContentTicket {
  ticketCount: number;
  waitForFree?: I_Waitfree;
  welcomeGiftTicketCount?: number;
  freeEpisodeCount?: number;
  allFree: boolean;
  freePublishing: boolean;
  waitForFreePlus?: boolean;
  badges?: I_Badge[];
}

export function getGiftTicketCount(data?: I_GiftTicket) {
  return data?.count || 0;
}

interface I_GiftTicket {
  count: number;
}

interface I_Purchase {
  content: {
    title: string;
  };
  my: {
    cash: {
      amount: number;
    };
    tickets: {
      possessionTicketCount: number;
      rentalTicketCount: number;
    };
    unreadLockedEpisodeCount: number;
  };
  products: {
    possessionTicket?: I_Voucher;
    rentalTicket?: I_Voucher;
  };
}

export function getLeftCash(data: I_Purchase) {
  return data.my.cash.amount;
}

export function getPurchase(data: I_Purchase, t: TFunction): IPurchase {
  const {
    unreadLockedEpisodeCount,
    tickets: {possessionTicketCount, rentalTicketCount},
  } = data.my;

  return {
    myInfo: {
      buyCount: possessionTicketCount || 0,
      rentCount: rentalTicketCount || 0,
      unreadEpisodeCount: unreadLockedEpisodeCount,
    },
    rent:
      data.products?.rentalTicket && getVoucher(data.products.rentalTicket, t),
    buy:
      data.products?.possessionTicket &&
      getVoucher(data.products.possessionTicket, t),
    content: {title: data.content.title},
  };
}

interface I_ContentProfile extends I_ContentMeta {
  seoKeywords: string[];
  originalProduct?: I_OriginalProduct;
  // 아래 두개는 recommendations를 쪼갠 데이터
  authorAnotherCollection: I_RecommendItem[];
  recommendationContents: I_RecommendItem;
  specialContent?: I_SpecialContent;
}
interface I_RecommendItem {
  title: string;
  authorName?: string;
  contents: I_ContentMeta[];
  torosLog?: I_TorosLog;
  authorType?: 'AUTHOR' | 'ILLUSTRATOR';
}
interface I_SpecialContent {
  bridgePageUrl: string;
  titleImage: string;
  characterImage: string;
  backgroundImage: string;
}

export function getEpisode({
  data,
  meta,
  contentId,
  isLogin,
  t,
}: {
  data?: I_Episode;
  meta?: I_ApiMeta;
  contentId: number;
  isLogin: boolean;
  t: TFunction;
}): IEpisode | undefined {
  if (!data) {
    return undefined;
  }
  const lastRead = getLastReadEpisode({
    contentId,
    data: data.lastRead,
    isLogin,
    t,
  });
  return {
    sort: meta?.sort === 'NO' ? 'oldest' : 'latest',
    lastRead,
    floatingButtonType: lastRead ? 'continueEpisode' : 'firstEpisode',
    firstEpisode: getEpisodeItem({item: data.first, t}),
    items: data.episodes
      ? data.episodes.map(item =>
          getEpisodeItem({
            item,
            t,
            now: getNowFromServer(data.responseDateTime),
          })
        )
      : [],
  };
}
function getLastReadEpisode({
  contentId,
  data,
  isLogin,
  t,
}: {
  contentId: number;
  data?: I_EpisodeItem;
  isLogin: boolean;
  t: TFunction;
}): ILastRead | undefined {
  const fromLocal = getLocalRecentItemByContentIdV4(contentId);
  const fromServer = data && getEpisodeItem({item: data, t});
  const useFromLocal =
    fromLocal &&
    (!isLogin ||
      !fromServer?.readDateTimeTs ||
      fromServer.readDateTimeTs <= fromLocal.readDateTime);
  return useFromLocal ? fromLocal!.episode : fromServer;
}

interface I_Episode {
  episodes: I_EpisodeItem[];
  lastRead?: I_EpisodeItem;
  first: I_EpisodeItem;
  responseDateTime?: string;
}

export function getIsLike(data?: I_Like): boolean {
  return !!data?.subscribe;
}

interface I_Like {
  id: number;
  subscribe: boolean;
}

export function getHasRedDot(data?: I_RedDot): boolean {
  return !!data?.noticeRedDot;
}

interface I_RedDot {
  noticeRedDot: boolean;
}

export function getKeywordDetail(data: I_SearchResult): IKeywordContent[] {
  return (data.content || []).map(item =>
    getContentProps(item, [
      'id',
      'sid',
      'title',
      'bgImg',
      'badges',
      'authors',
      'mainTitleImg',
      'mainImg',
      'genre',
    ])
  );
}

export function getContentAdBanner(contentId: number, data: I_AdBanner) {
  return (
    data && {
      banner: getAdBanner({page: Page.Content, data, needDefaultType: true}),
      contentId,
    }
  );
}

function getWaitfreeData(data: I_Waitfree) {
  const waitfree = getWaitfree(data);
  const {
    intervalMinutes,
    excludedEpisodeCount,
    isCharging,
    chargeAtTs,
    nowFromServer,
  } = waitfree;
  const period = getWaitfreePeriodData(intervalMinutes);
  const hasWaitfreeTicket = !isCharging;

  return {
    waitfreeCount: period.count,
    waitfreeTimeUnit: period.timeUnitText,
    excludedEpisodeCount,
    hasWaitfreeTicket,
    chargeAtTs,
    nowFromServer,
  };
}

export function getContentAddedTicketHistory(
  data: I_ContentAddedTicketHistory,
  t: TFunction
): {ticketInfo: IContentTicketInfo; contents: IContentAddedTicketItem[]} {
  const now = Date.now();

  return {
    ticketInfo: {
      canPurchaseTicket: data.ticketPurchasable,
      totalTicketCount: data.ticketCount,
      rentCount: data.rentalTicketCount,
      buyCount: data.possessionTicketCount,
      giftCount: data.giftTicketCount || 0,
      waitfree: data.waitForFree
        ? getWaitfreeData(data.waitForFree)
        : undefined,
    },
    contents:
      data?.tickets?.map(item => {
        const type = toCamelCase(item.type) as ITicketType;
        const expireDt = item.expiresDateTime;
        const isEventGift = type === 'gift';

        return {
          purchaseId: item.purchaseId,
          type,
          ticketCount: item.initCount,
          hasRemainTicket: item.remainCount > 0,
          isEventGift,
          isNewbie: type === 'welcomeGift',
          isExpired: item.expired,
          isUsed: item.used,
          isRefunded: item.refunded,
          addedDate: formatFromServerDate(
            item.chargedDateTime,
            SHORT_DATE_FORMAT
          ),
          remainExpireText:
            isEventGift && !!expireDt
              ? getRemainText({endDate: expireDt, now, isShortUnit: true}, t)
              : undefined,
          timestamp: getTimeStamp(item.chargedDateTime),
          itemType: 'added',
        };
      }) || [],
  };
}

interface I_ContentAddedTicketHistory {
  content: Pick<
    I_ContentMeta,
    'adult' | 'featuredCharacterImageA' | 'id' | 'seoId' | 'title'
  >;
  ticketCount: number;
  giftTicketCount?: number;
  rentalTicketCount: number;
  possessionTicketCount: number;
  waitForFree?: I_Waitfree;
  ticketPurchasable: boolean;
  tickets: Array<{
    purchaseId?: number;
    type: string;
    initCount: number;
    remainCount: number;
    chargedDateTime: string;
    expiresDateTime?: string;
    refunded: boolean;
    expired: boolean;
    used: boolean;
  }>;
}

export function getContentUsedTicketItems(
  items: I_ContentUsedTicketItem[] = [],
  t: TFunction
): IContentUsedTicketItem[] {
  return items.map(item => ({
    id: item.id,
    episode: getEpisodeItem({item: item.episode, t}),
    content: getContentProps(item.content, ['id', 'title', 'sid']),
    type: toCamelCase(item.ticketType) as ITicketType,
    usedDate: formatFromServerDate(item.usedDateTime, SHORT_DATE_FORMAT),
    timestamp: getTimeStamp(item.usedDateTime),
    itemType: 'used',
  }));
}

interface I_ContentUsedTicketItem {
  id: number;
  content: I_ContentMeta;
  episode: I_EpisodeItem;
  licenseExpiresDateTime: string;
  ticketType: string;
  usedDateTime: string;
}

export function getContentTicketDetail(
  data: I_ContentTicketDetail
): IContentTicketDetail {
  return {
    purchaseId: data.purchaseId,
    date: formatFromServerDate(data.purchasedDateTime, SHORT_DATE_FORMAT),
    ticketCount: data.initialRefundableCount,
    ticketBonus: data.initialNonRefundableCount,
    refundableTicketCount: data.remainRefundableCount,
    cashPerOneTicket: data.ticketPrice,
    refundCash: data.totalCashAmount,
    expiredEventCash: data.expiredCashAmount,
    refundableTotalCash: data.refundableCashAmount,
    content: getContentProps(data.content, ['title']),
  };
}

interface I_ContentTicketDetail {
  purchaseId: number;
  purchasedDateTime: string;
  initialRefundableCount: number;
  initialNonRefundableCount: number;
  remainRefundableCount: number;
  ticketPrice: number;
  totalCashAmount: number;
  expiredCashAmount: number;
  refundableCashAmount: number;
  content: I_ContentMeta;
}

export function getIsFirstRead(data: I_IsFirstRead) {
  return data.firstRead;
}
interface I_IsFirstRead {
  firstRead: boolean;
}

export function getViewData({
  data,
  nonce,
  timestamp,
  t,
}: {
  data: I_ViewData;
  nonce: string;
  timestamp: string;
  t: TFunction;
}): IViewData {
  const isPageViewDefault = data.content.defaultView === 'TURNING_PAGE';

  return {
    content: {
      ...getContentProps(data.content, [
        'sid',
        'promotionVideo',
        'synopsis',
        'badges',
      ]),
      isPageViewDefault,
    },
    contentBmView: {
      isAllFree: data.contentBMView.allFree,
      freeEpisodeCount: data.contentBMView.freeEpisodeCount,
      waitfree:
        data.contentBMView.waitForFreeUsed &&
        getWaitfree(data.contentBMView.waitForFreeUsed),
    },

    episode: getEpisodeItem({item: data.episode, t}),
    nextEpisode:
      data.nextEpisode && getEpisodeItem({item: data.nextEpisode, t}),
    ...(!isPageViewDefault && data.media
      ? getViewerResources({
          episodeId: data.episode.id,
          media: data.media,
          nonce,
          timestamp,
          limit: Math.ceil(data.media.files.length / 2),
        })
      : {images: []}),
  };
}
interface I_ViewContent extends I_Content {
  defaultView: string;
}
interface I_ViewData {
  content: I_ViewContent;
  contentBMView: {
    allFree: boolean;
    freeEpisodeCount: number;
    waitForFreeUsed?: I_Waitfree;
  };
  media: I_ViewerImage;
  episode: I_EpisodeItem;
  nextEpisode?: I_EpisodeItem;
}

export function getFreeTicketInfo(data: I_FreeTicketInfo): IFreeTicketInfo {
  return {
    freeTickets: data.freeTickets.map(({ticketCount, expireDateTime, id}) => ({
      ticketCount,
      expiredDate: formatFromServerDate(
        expireDateTime,
        DEFAULT_SHORT_DATETIME_FORMAT
      ),
      ticketId: id,
    })),
    showFreeTicketPopup: data.received,
  };
}

interface I_FreeTicketInfo {
  freeTickets: Array<{
    id: string;
    ticketCount: number;
    expireDateTime: string;
  }>;
  received: boolean;
  totalTicketCount: number;
}
